import { Component } from '@theme/component';
import { observerElement, prefersReducedMotion } from '@theme/utilities';

/**
 * A before/after image comparison slider.
 *
 * Reveals the "after" image by clipping it against a draggable handle. The
 * current position is exposed through the `--compare-position` custom property so the
 * actual clipping and handle placement stay in CSS (no layout thrash on drag).
 *
 * Attributes:
 * - `vertical` - Switch the drag axis from horizontal to vertical.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} frame - The media frame the comparison is measured against.
 * @property {HTMLElement} handle - The draggable handle (also the slider thumb).
 *
 * @extends Component<Refs>
 */
class ImageComparisonComponent extends Component {
  requiredRefs = ['frame', 'handle'];

  /** Step used for keyboard navigation, in percent. */
  #step = 1;
  #stepLarge = 10;

  /** @type {number | null} Pending rAF id while dragging. */
  #frame = null;
  /** @type {PointerEvent | null} Latest pointer event awaiting a frame. */
  #pointer = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.abortController;
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });

    this.#revealOnView();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
  }

  get #vertical() {
    return this.hasAttribute('vertical');
  }

  /**
   * The handle starts at the leading edge via CSS (--compare-position: 0%).
   * The first time the component scrolls into view we animate it to the
   * configured initial position. Falls back to setting the value immediately
   * when motion is reduced or observers are unavailable.
   */
  #revealOnView() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      this.#setPosition(this.#initialPosition);
      return;
    }

    this.#observer = observerElement({
      element: this,
      callback: () => this.#setPosition(this.#initialPosition),
    });
  }

  /** @type {IntersectionObserver | undefined} */
  #observer;

  get #initialPosition() {
    const value = parseFloat(getComputedStyle(this).getPropertyValue('--compare-initial-position'));
    return Number.isFinite(value) ? value : 50;
  }

  #onPointerDown = (event) => {
    // Ignore clicks on interactive content (e.g. linked labels).
    if (event.target instanceof HTMLAnchorElement) return;

    this.setPointerCapture(event.pointerId);
    this.classList.add('is-dragging');
    this.#updateFromPointer(event);

    const { signal } = this.abortController;
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointercancel', this.#onPointerUp, { signal });
  };

  #onPointerMove = (event) => {
    this.#pointer = event;
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#flushPointer);
  };

  #flushPointer = () => {
    this.#frame = null;
    if (this.#pointer) this.#updateFromPointer(this.#pointer);
  };

  #onPointerUp = (event) => {
    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }
    this.#pointer = null;
    this.classList.remove('is-dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
  };

  #onKeyDown = (event) => {
    const vertical = this.#vertical;
    const decrease = vertical ? 'ArrowUp' : 'ArrowLeft';
    const increase = vertical ? 'ArrowDown' : 'ArrowRight';

    let next;
    switch (event.key) {
      case decrease:
        next = this.#position - (event.shiftKey ? this.#stepLarge : this.#step);
        break;
      case increase:
        next = this.#position + (event.shiftKey ? this.#stepLarge : this.#step);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = 100;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#setPosition(next);
  };

  /** Current position in percent, read back from the custom property. */
  get #position() {
    const value = parseFloat(this.style.getPropertyValue('--compare-position'));
    return Number.isFinite(value) ? value : this.#initialPosition;
  }

  #updateFromPointer(event) {
    const rect = this.refs.frame.getBoundingClientRect();
    let percentage = this.#vertical
      ? ((event.clientY - rect.top) / rect.height) * 100
      : ((event.clientX - rect.left) / rect.width) * 100;

    if (!this.#vertical && document.dir === 'rtl') percentage = 100 - percentage;

    this.#setPosition(percentage);
  }

  /** @param {number} value - Target position in percent. */
  #setPosition(value) {
    const clamped = Math.min(100, Math.max(0, value));
    this.style.setProperty('--compare-position', `${clamped}%`);
    this.refs.handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
  }
}

if (!customElements.get('image-comparison-component')) {
  customElements.define('image-comparison-component', ImageComparisonComponent);
}
